import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { OrderType, Pages } from '../../../types/enums';
import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { DialogCreators } from '../../../utils/dialog-creators';
import { IDataItem, IDataOrderTypeSubType } from '../../../models/data.interface';
import { ISegmentedButtonData } from '../SegmentedButton/segmented-button.interface';
import { SegmentedButton } from '../SegmentedButton/segmented-button';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { Dialog } from '../../modals/Dialog/dialog';
import { Router } from '../../../core/router';
import { DataLayerService } from '../../../services/data-layer-service';

export interface IOrderTypeSelectorOptions {
    /** When false, selecting a new order type does not mutate the order model or emit ORDER_TYPE_CHANGED; the caller is notified via onSelectionChange instead. Defaults to true. */
    applyImmediately?: boolean;
    /** Invoked with the newly selected order type ID when applyImmediately is false. */
    onSelectionChange?: (orderTypeId: OrderType) => void;
}

/**
 * Handles the selection and management of order types.
 */
export class OrderTypeSelector {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _order: Order;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _containerElement: HTMLElement;
    private readonly _applyImmediately: boolean;
    private readonly _onSelectionChange?: (orderTypeId: OrderType) => void;
    private _pendingOrderTypeId: OrderType;
    private _randomId: number = 0;

    /**
     * Initializes an instance of the OrderTypeSelector class.
     * @param eventBus - The event bus for handling events.
     * @param dataService - The service handling data-related operations.
     * @param orderService - The service handling order-related operations.
     * @param loyaltyProvider - The loyalty provider for handling loyalty-related operations.
     * @param initialOrderTypeId - The initial selected order type ID.
     * @param availableOrderTypes - List of available order type subtypes.
     * @param options - Optional behavior overrides (e.g. defer applying the selection to the order model).
     */
    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        dataLayerService: DataLayerService,
        order: Order,
        loyaltyProvider: ILoyaltyProvider,
        orderTypesSubTypes: IDataOrderTypeSubType[],
        orderTypeId: OrderType = OrderType.DELIVERY,
        options?: IOrderTypeSelectorOptions,
    ) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._dataLayerService = dataLayerService;
        this._order = order;
        this._loyaltyProvider = loyaltyProvider;
        this._applyImmediately = options?.applyImmediately ?? true;
        this._onSelectionChange = options?.onSelectionChange;
        this._pendingOrderTypeId = orderTypeId;
        this._containerElement = this._createOrderTypeSelector(orderTypeId, orderTypesSubTypes);
        this._setupEventListeners();
    }

    /**
     * Gets the HTML container element for the order type selector.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Sets up event listeners for handling order type changes.
     */
    private _setupEventListeners() {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._handleOrderTypeChanged);
    }

    /**
     * Creates the order type selector element.
     * @param selectedOrderTypeId - The currently selected order type ID.
     * @param availableOrderTypes - List of available order type subtypes.
     * @returns The HTML element representing the segmented button.
     */
    private _createOrderTypeSelector(selectedOrderTypeId: OrderType, availableOrderTypes: IDataOrderTypeSubType[]): HTMLElement {
        this._randomId = Math.floor(Math.random() * 99999) + 1;

        // Left-to-right by SortingIndex; ties break by order type ID.
        const sortedOrderTypes = [...availableOrderTypes].sort((a, b) =>
            (a.SortingIndex ?? 0) - (b.SortingIndex ?? 0) || a.OrderTypeId - b.OrderTypeId);

        const segmentedData: ISegmentedButtonData = {
            buttonId: `order_type_selector_${this._randomId}`,
            ariaGroupLabel: "Order Type Selector",
            buttons: sortedOrderTypes.map(orderType => ({
                id: orderType.OrderTypeId,
                text: this._localizationService.GetName(orderType.Names),
                selected: orderType.OrderTypeId === selectedOrderTypeId,
                ltagj: orderType.Names,
                function: (): boolean => this._handleOrderTypeSelection(orderType.OrderTypeId)
            }))
        };

        return new SegmentedButton(segmentedData, this._localizationService).element;
    }

    /**
     * Handles the order type change event by simulating a button click.
     * @param orderTypeId - The new order type ID.
     */
    private readonly _handleOrderTypeChanged = (orderTypeId: OrderType): void => {
        if (this._order.getProperty("OrderTypeID") == orderTypeId)
            return;

        this._order.setProperty("OrderTypeID", orderTypeId);
        const button = document.querySelector(`#btn_order_type_selector_${this._randomId}_${orderTypeId}`) as HTMLButtonElement | undefined;
        if (!button?.classList.contains("active"))
            button?.click();
    }

    /**
     * Handles the selection of a new order type.
     * @param orderTypeId - The selected order type ID.
     * @returns True if the order type change was successful, false otherwise.
     */
    private readonly _handleOrderTypeSelection = (orderTypeId: OrderType): boolean => {
        const isLocationPage = Router.path === Pages.LOCATIONS;
        const currentOrderTypeId = this._applyImmediately ? this._order.getProperty("OrderTypeID") : this._pendingOrderTypeId;

        const isSameOrderType = currentOrderTypeId === orderTypeId;
        const storeValid = this._validateStoreHasOrderType(orderTypeId);
        const itemsValid = this._validateOrderItemsForOrderType(orderTypeId);

        if ((isLocationPage && (!storeValid || !itemsValid)) ||
            (!isLocationPage && (isSameOrderType || !storeValid || !itemsValid))) {
            return false;
        }

        const orderType = OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getBrandProperty("OrderTypeSubTypes"));
        if (!orderType)
            return false;

        if (isLocationPage) {
            this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED_LOCATIONS, orderTypeId);
        } else if (this._applyImmediately) {
            // If checkout page, and clicking away from delivery, clear the address
            if (currentOrderTypeId === OrderType.DELIVERY) {
                this._order.setAddress(null);
            }

            this._order.setProperty("OrderTypeID", orderTypeId);
            this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, orderTypeId, this._loyaltyProvider);
        } else {
            this._pendingOrderTypeId = orderTypeId;
            this._onSelectionChange?.(orderTypeId);
        }

        return true;
    }

    /**
     * Validates whether the currently selected store supports the given order type.
     * If the store does not support the order type and the user is not on the locations page,
     * a dialog is shown to inform them and the method returns false.
     *
     * @param {OrderType} orderTypeId - The ID of the order type to validate.
     * @returns {boolean} `true` if the store supports the order type, no store is selected, 
     *                    or the user is on the locations page; otherwise `false`.
     */
    private _validateStoreHasOrderType(orderTypeId: OrderType): boolean {
        const currentStoreKey = this._data.storeKey;
        const orderTypeSubTypes = this._data.getProperty("OrderTypeSubTypes") ?? [];
        const isOrderTypeAvailable = orderTypeSubTypes.some(x => x.OrderTypeId === orderTypeId);
        const isOnLocationPage = Router.path === Pages.LOCATIONS;

        // Pass-through if no store is selected, or the store supports the order type, or user is on locations page
        if (!currentStoreKey || isOrderTypeAvailable || isOnLocationPage)
            return true;

        // Fallback to brand-level config for order type name resolution
        const brandOrderTypeSubTypes = this._data.getBrandProperty("OrderTypeSubTypes") ?? [];
        const languageCode = this._context.getProperty("languageCode") ?? "en";
        const orderTypeName = OnlineOrderingUtil.GetOrderTypeName(orderTypeId, brandOrderTypeSubTypes, this._localizationService);

        // Show error dialog with localized message
        const errorMessage = this._localizationService.names("Error_OrderTypeUnavailableAtStore")
            .replace("{{order_type}}", orderTypeName);

        new Dialog(
            errorMessage,
            [
                { label: this._localizationService.names("OK").toUpperCase(), value: "yes" },
            ]
        );
        return false;
    }

    /**
     * Validates if the order items are compatible with the new order type.
     * @param orderTypeId - The new order type ID.
     * @returns True if all order items are valid for the new order type, false otherwise.
     */
    private _validateOrderItemsForOrderType(orderTypeId: OrderType): boolean {
        for (const orderItem of this._order.getProperty("Items")) {
            const menuItem = this._data.getProperty("Items").find(x => x.ItemId === orderItem.ItemId);

            if (menuItem &&
                (!OnlineOrderingUtil.isItemValidForOrderType(menuItem, orderTypeId) ||
                    !OnlineOrderingUtil.ItemHasSize(menuItem, orderTypeId, orderItem.SizeId, true))) {
                this._displayOrderTypeChangeError(menuItem, orderItem, orderTypeId);
                return false;
            }
        }
        return true;
    }

    /**
     * Displays an error message when an order type change is not allowed due to incompatible items.
     * @param menuItem - The menu item that caused the restriction.
     * @param orderItem - The corresponding order item.
     * @param orderTypeId - The attempted new order type ID.
     */
    private _displayOrderTypeChangeError(menuItem: IDataItem, orderItem: any, orderTypeId: number): void {
        const currentOrderTypeId = this._order.getProperty("OrderTypeID");
        const orderTypeSubTypes = this._data.getProperty("OrderTypeSubTypes");
        const languageCode = this._context.getProperty("languageCode");

        const itemSize = OnlineOrderingUtil.GetItemSize(menuItem, currentOrderTypeId, orderItem.SizeId, true);
        const size = this._data.getProperty("Sizes").find(size => size.SizeId === itemSize?.SizeId);
        const sizeName = size ? this._localizationService.GetName(size.Names) : "";
        const itemName = this._localizationService.GetName(menuItem.Names);
        const newOrderTypeName = OnlineOrderingUtil.GetOrderTypeName(orderTypeId, orderTypeSubTypes, this._localizationService);

        const errorMessage = this._localizationService.names("Error_ChangeOrderTypeCartError")
            .replace("{{size}}", sizeName)
            .replace("{{item}}", itemName)
            .replace("{{order_type}}", newOrderTypeName);

        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayerService,
            errorMessage,
            true
        );
    }
}